import * as THREE from 'three';
import { BufferGeometryUtils } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
/* ============================================================
   地面與天空 —— 廢棄都市的空曠戰場
   ============================================================

   使用者 2026-08-04：場地要**空曠、看不到邊**，但畫風照**廢棄都市**
   那一套（不是霓虹黑底那一套）。所以地面分四層：

     ① `asphaltTex()`  柏油／混凝土：接縫、裂縫、油漬、碎石
     ② `decalTex()`    整場尺寸的一張：彈坑、車轍、斑駁、外圍壓暗
     ③ 光影層          `arena.ts` 用同一張 `lightTex()` 緩慢飄移（雲影）
     ④ `farRuins()`    場外的遠景廢墟剪影 ＋ `skyTex()` 灰燼天空

   ## 兩個踩過的坑

   1. **`CanvasTexture` 一定要設 `encoding = sRGBEncoding`。**
      three r128 預設 LinearEncoding：一張近黑的貼圖會被當成 linear 直接
      輸出，亮成中灰 —— 而且看起來只是「有點亮」，不會有任何錯誤。
      2026-08-04 的第一版空曠場地就是這樣變成一片亮藍格線的。
   2. **等距、等寬、高對比的線條無論貼什麼顏色都讀成 grid helper**
      （使用者 2026-08-03 拿掉 GridHelper 的理由）。所以接縫要
      **粗細不一、濃度不一、週期夠大**（26 m 一條，比鏡頭視野小不了多少）。

   ⚠️ **Node 沒有 canvas**：headless 模擬會走到這裡，每個函式先問
   `typeof document`，拿不到就回 null。 */

var _asphalt = null, _decal = null, _light = null, _sky = null;

function cv(w, h): any {
  if (typeof document === 'undefined') return null;
  var c: any = document.createElement('canvas');
  c.width = w; c.height = h; return c;
}
function tex(c, wrap?) {
  var t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = wrap === false ? THREE.ClampToEdgeWrapping : THREE.RepeatWrapping;
  t.encoding = (THREE as any).sRGBEncoding;      // 見檔頭第 1 點
  t.anisotropy = 8;
  return t;
}

/** 一張柏油貼圖涵蓋多少公尺（`arena.ts` 設 repeat 時要用同一個數字）。 */
export var TILE_M = 52;

/** 把一段繪圖**繞回畫九次**（3×3 個平移），讓跨越邊界的圖形在對面接得上。

    使用者 2026-08-04：「地上水攤在不同塊拼貼在一起時會發生斷層」。
    這張貼圖是 `RepeatWrapping`、52 m 鋪一次，任何大尺度的圖形（補丁、油漬、
    沙塵帶、水窪）畫到邊界就被切掉，對面不會接上 —— 畫面上是每 52 m 一條
    直線的斷層，而且**越大的圖形越明顯**（小碎石與裂縫被切掉看不出來）。

    ⚠️ **亂數一定要在 `fn` 外面取好**。在 `fn` 裡呼叫 `rnd()` 的話九次會畫出
    九個不同的圖形，貼圖密度直接變九倍 —— 而且它看起來只是「怎麼變得好髒」。 */
function tile9(g, S, fn) {
  for (var i = -1; i <= 1; i++) for (var j = -1; j <= 1; j++) {
    g.save(); g.translate(i * S, j * S); fn(); g.restore();
  }
}

/** 柏油／混凝土路面。**這一層的工作是「暗而有顆粒」** ——
    威脅色與玩家的青綠要能從它上面跳出來。 */
export function asphaltTex() {
  if (_asphalt !== null || typeof document === 'undefined') return _asphalt;
  var S = 1024, c = cv(S, S), g = c.getContext('2d');
  var seed = 1337;
  function rnd() { seed = (seed * 1664525 + 1013904223) & 0x7fffffff; return seed / 0x7fffffff; }

  /* ⚠️ 底色是**混凝土**不是柏油黑。三層乘算（貼圖 × 貼花 × 雲影）疊起來
     只會越來越暗，所以每一層都要留亮度 —— 第一版底色 #23262c（14%）
     疊完之後整片地面剩 6% 反照率，畫面上是一片純黑。 */
  /* ⚠️ **底色是冷灰**（2026-08-04 從暖灰 `#4a463f` 換回來：使用者要
     機甲風的冷調，而地面佔畫面面積最大，暖灰底色會把整張圖拉黃）。
     **明度不動**（兩者的 luma 都是 70）—— 這一層的亮度是三層乘算
     （貼圖 × 貼花 × 雲影）算過的，動明度會讓地面整片變黑。 */
  g.fillStyle = '#42464e'; g.fillRect(0, 0, S, S);
  /* 大塊的鋪面補丁：深淺不一的方塊，邊緣不對齊 —— 修過很多次的路面 */
  for (var p = 0; p < 26; p++) {
    var pw = 90 + rnd() * 300, ph = 90 + rnd() * 300;
    var v = 60 + Math.round(rnd() * 30);
    /* 補丁往**藍**偏一階（原本是往紅偏）：同一批混凝土補了不同年份，
       色偏要跟底色同一個方向，不然補丁讀成髒污。 */
    g.fillStyle = 'rgba(' + (v - 8) + ',' + (v - 1) + ',' + (v + 8) + ',' + (0.3 + rnd() * 0.5) + ')';
    var qx = rnd() * S, qy = rnd() * S;
    tile9(g, S, function () { g.fillRect(qx, qy, pw, ph); });
  }
  /* 接縫：兩條，粗細與濃度逐段變化（見檔頭第 2 點）。
     ⚠️ **`k` 只跑 0 與 1**。舊版跑到 2（也就是同時畫 x=0 與 x=S），
     而在繞回的貼圖上那是**同一條線** —— 兩邊各被切一半、亂數的寬度與濃度
     又不一樣，拼起來就是一條對不齊的接縫（2026-08-04 量到的殘餘斷層有
     一半來自這裡）。改成只畫 x=0 並用 `tile9` 繞回，兩半自然一致。 */
  g.lineCap = 'butt';
  for (var k = 0; k <= 1; k++) {
    for (var sg = 0; sg < 10; sg++) {
      var a2 = 0.10 + rnd() * 0.26, y0 = sg * S / 10, y1 = (sg + 1) * S / 10;
      var lw = 2 + rnd() * 2.5, kk = k;
      tile9(g, S, function () {
        g.lineWidth = lw;
        g.strokeStyle = 'rgba(12,14,18,' + a2 + ')';
        g.beginPath(); g.moveTo(kk * S / 2, y0); g.lineTo(kk * S / 2, y1); g.stroke();
        g.beginPath(); g.moveTo(y0, kk * S / 2); g.lineTo(y1, kk * S / 2); g.stroke();
      });
    }
  }
  /* 油漬與水痕 */
  for (var s = 0; s < 70; s++) {
    var x = rnd() * S, y = rnd() * S, r = 24 + rnd() * 130;
    var dark = rnd() < 0.72;
    var a = (dark ? 0.22 : 0.12) * (0.4 + rnd() * 0.6);
    tile9(g, S, function () {
      /* 漸層的座標是**畫布座標**，所以要在每一次平移之後重建 ——
         建好放外面重用的話九份都會畫在同一個位置。 */
      var rg = g.createRadialGradient(x, y, 0, x, y, r);
      rg.addColorStop(0, (dark ? 'rgba(8,10,14,' : 'rgba(150,162,178,') + a + ')');
      rg.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = rg; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
    });
  }
  /* 乾掉的沙塵堆積：**風把塵吹成一道一道的**。這一層是「近景有資訊」的
     主力 —— 鏡頭裡地面佔的面積最大，而裂縫與碎石在 60 px 的機體旁邊
     太細，讀不到。大尺度的明暗斑才是玩家真的看得到的紋理。 */
  for (var dn = 0; dn < 34; dn++) {
    var dx0 = rnd() * S, dy0 = rnd() * S, dl = 160 + rnd() * 420, da = rnd() * 0.9 - 0.45;
    var dAlpha = 0.06 + rnd() * 0.10, dTop = -12 - rnd() * 26, dH = 24 + rnd() * 52;
    tile9(g, S, function () {
      var dg = g.createLinearGradient(dx0, dy0, dx0 + Math.cos(da) * dl, dy0 + Math.sin(da) * dl);
      dg.addColorStop(0, 'rgba(118,132,154,0)');
      dg.addColorStop(0.5, 'rgba(118,132,154,' + dAlpha + ')');
      dg.addColorStop(1, 'rgba(118,132,154,0)');
      g.fillStyle = dg;
      g.save(); g.translate(dx0, dy0); g.rotate(da);
      g.fillRect(-dl * 0.1, dTop, dl, dH);
      g.restore();
    });
  }
  /* 積水窪（2026-08-04 質感第二波）。**這一層同時是兩張圖**：
     它在反照率上是「暗一階的柏油」（濕的路面本來就比乾的暗），
     而 `render/tex-normal.ts` 的 `roughFrom()` 是照亮度算粗糙度的 ——
     所以同一批暗斑會自動變成粗糙度 0.3 的光滑面，也就是會反射天空的積水。

     ⚠️ 形狀要**扁而不規則**：正圓的暗斑讀成油漬不是水窪。用兩個橢圓疊
     一個偏移的第三個，邊緣靠徑向漸層糊掉 —— 水窪沒有硬邊。
     ⚠️ 數量壓在 7 個、濃度壓到 0.30：這張圖鋪的是 52 m 見方，而**水窪跟
     建築的陰影在俯視鏡頭下是同一種東西**（都是地面上的暗斑）——
     11 個 0.42 的版本讓整張圖讀成豹紋，而且分不出哪些是影子。
     水窪的工作是「有幾處在反光」，不是「地面很髒」。 */
  /* ⚠️ **一定要繞回畫**（每一瓣畫九次：3×3 個平移）。這張貼圖是
     `RepeatWrapping`、52 m 鋪一次，跨過邊界的水窪如果只畫一次，
     另外半邊不會出現在對面 —— 畫面上就是使用者 2026-08-04 回報的
     「不同塊拼貼在一起時會發生斷層」：地面每 52 m 有一條水窪被切斷的直線。
     九次畫圖只在建圖時跑一次，成本可以忽略。 */
  for (var pd = 0; pd < 7; pd++) {
    var px0 = rnd() * S, py0 = rnd() * S, prx = 60 + rnd() * 130, pry = prx * (0.42 + rnd() * 0.4);
    var pa = rnd() * Math.PI;
    for (var lobe = 0; lobe < 3; lobe++) {
      var ox = lobe ? (rnd() - 0.5) * prx * 1.1 : 0, oy = lobe ? (rnd() - 0.5) * pry * 1.1 : 0;
      var kx = lobe ? 0.55 + rnd() * 0.4 : 1;
      tile9(g, S, function () {
        g.save(); g.translate(px0 + ox, py0 + oy); g.rotate(pa);
        var pg2 = g.createRadialGradient(0, 0, 0, 0, 0, prx * kx);
        pg2.addColorStop(0, 'rgba(9,13,19,0.30)');
        pg2.addColorStop(0.62, 'rgba(9,13,19,0.23)');
        pg2.addColorStop(1, 'rgba(9,13,19,0)');
        g.fillStyle = pg2;
        g.scale(1, (pry * kx) / (prx * kx));
        g.beginPath(); g.arc(0, 0, prx * kx, 0, Math.PI * 2); g.fill();
        g.restore();
      });
    }
  }
  /* 裂縫：折線，一路變細 */
  g.lineCap = 'round';
  for (var cr = 0; cr < 30; cr++) {
    /* 先把整條折線的頂點算好，再用 `tile9` 一次畫九份 ——
       邊算邊畫的話九份會走成九條不同的裂縫（見 `tile9` 的警告）。 */
    var px = rnd() * S, py = rnd() * S, ang = rnd() * Math.PI * 2;
    var pts: number[][] = [[px, py]];
    for (var seg = 0; seg < 8; seg++) {
      px += Math.cos(ang) * (14 + rnd() * 40); py += Math.sin(ang) * (14 + rnd() * 40);
      pts.push([px, py]); ang += (rnd() - 0.5) * 1.1;
    }
    tile9(g, S, function () {
      g.strokeStyle = 'rgba(10,12,16,0.30)';
      for (var q = 1; q < pts.length; q++) {
        g.lineWidth = 1.9 - (q - 1) * 0.2;
        g.beginPath(); g.moveTo(pts[q - 1][0], pts[q - 1][1]); g.lineTo(pts[q][0], pts[q][1]); g.stroke();
      }
    });
  }
  /* 碎石與細噪點：末日的路面是**破的**，不是一塊乾淨的水泥板 */
  for (var st = 0; st < 2600; st++) {
    var sx = rnd() * S, sy = rnd() * S, ss = 1.5 + rnd() * 4;
    var sv = rnd() < 0.55 ? 26 : 172;
    g.fillStyle = 'rgba(' + (sv - 8) + ',' + sv + ',' + (sv + 10) + ',' + (0.12 + rnd() * 0.26) + ')';
    g.fillRect(sx, sy, ss, ss * (0.6 + rnd() * 0.8));
  }
  /* 分道線：**褪色的黃漆**（`world_sample.html` 的 `rgba(196,164,86,.42)`）。
     冷白讀成「新畫的標線」，泛黃才讀成「沒人維護了十年」。
     兩側的白邊線再淡一階 —— 一條中線加兩條邊線就夠說明「這裡曾經是路」。 */
  /* 冷底之後這條黃線是地面**唯一**的暖點，所以濃度要壓（.24 → .15）：
     整片地面重複鋪 52 m 一張，太濃會累積成一層黃紗蓋掉冷調。 */
  g.fillStyle = 'rgba(176,150,88,.15)';
  for (var m = 0; m < S; m += 150) g.fillRect(S * 0.5 - 5, m, 10, 74);
  g.fillStyle = 'rgba(146,154,168,.13)';
  g.fillRect(S * 0.5 - 118, 0, 7, S); g.fillRect(S * 0.5 + 111, 0, 7, S);

  _asphalt = tex(c);
  return _asphalt;
}

/** 整場尺寸的一張：彈坑、車轍、外圍壓暗。**乘算**到地面上
    （白＝不變、暗＝髒／凹），所以柏油底色要比目標亮一階。 */
export function decalTex() {
  if (_decal !== null || typeof document === 'undefined') return _decal;
  var S = 2048, c = cv(S, S), g = c.getContext('2d');
  var seed = 90210;
  function rnd() { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; }
  /* 底色接近白＝「這一層預設不改變地面」。暗的地方才是彈坑與車轍。 */
  g.fillStyle = '#d6d9de'; g.fillRect(0, 0, S, S);
  /* 底層斑駁 */
  for (var i = 0; i < 700; i++) {
    var x = rnd() * S, y = rnd() * S, r = 30 + rnd() * 260;
    var rg = g.createRadialGradient(x, y, 0, x, y, r);
    var v = rnd() < 0.6 ? '0,0,0' : '255,255,255';
    rg.addColorStop(0, 'rgba(' + v + ',' + (0.03 + rnd() * 0.09) + ')');
    rg.addColorStop(1, 'rgba(' + v + ',0)');
    g.fillStyle = rg; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
  }
  /* 彈坑：暗心 + 一圈揚起的亮邊 */
  for (var b = 0; b < 90; b++) {
    var cx = rnd() * S, cy = rnd() * S, cr = 12 + rnd() * 52;
    var g2 = g.createRadialGradient(cx, cy, cr * .15, cx, cy, cr);
    g2.addColorStop(0, 'rgba(0,0,0,.44)'); g2.addColorStop(.62, 'rgba(0,0,0,.17)');
    g2.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = g2; g.beginPath(); g.arc(cx, cy, cr, 0, Math.PI * 2); g.fill();
    g.strokeStyle = 'rgba(255,255,255,.14)'; g.lineWidth = 2.4;
    g.beginPath(); g.arc(cx, cy, cr * .66, 0, Math.PI * 2); g.stroke();
  }
  /* 車轍：長弧線，暗示曾經有東西輾過整片場地 */
  for (var t = 0; t < 46; t++) {
    g.strokeStyle = 'rgba(0,0,0,' + (0.04 + rnd() * 0.08) + ')';
    g.lineWidth = 4 + rnd() * 10;
    g.beginPath();
    var sx = rnd() * S, sy = rnd() * S;
    g.moveTo(sx, sy);
    for (var j = 0; j < 6; j++) { sx += (60 + rnd() * 200) * (rnd() < .5 ? 1 : -1); sy += (60 + rnd() * 200) * (rnd() < .5 ? 1 : -1); g.lineTo(sx, sy); }
    g.stroke();
  }
  /* 外圍壓暗：**邊界不畫警示帶**（使用者 2026-08-04），
     靠這一圈漸暗 ＋ 霧讓場地「沒有盡頭」。 */
  /* ⚠️ 壓暗只到 0.46（原本 0.72）。**霧才是「看不到邊」的機制**，
     貼花壓黑只是把場地外圍變成一圈死黑 —— 而死黑在畫面上讀成
     「地圖到這裡就沒了」，正好跟「無限延伸」相反。 */
  var vg = g.createRadialGradient(S / 2, S / 2, S * 0.34, S / 2, S / 2, S * 0.58);
  vg.addColorStop(0, 'rgba(0,0,0,0)');
  vg.addColorStop(0.74, 'rgba(4,7,10,0.10)');
  vg.addColorStop(0.90, 'rgba(3,6,8,0.28)');
  vg.addColorStop(1, 'rgba(2,4,6,0.46)');
  g.fillStyle = vg; g.fillRect(0, 0, S, S);

  _decal = tex(c, false);
  return _decal;
}

/** 雲影：大而少的柔光斑，`arena.animField()` 讓它緩慢飄。
    **這就是使用者要的「光影」** —— 空曠場地唯一的動態。 */
export function lightTex() {
  if (_light !== null || typeof document === 'undefined') return _light;
  var S = 512, c = cv(S, S), g = c.getContext('2d');
  var seed = 24601;
  function rnd() { seed = (seed * 1664525 + 1013904223) & 0x7fffffff; return seed / 0x7fffffff; }
  /* 底是白＝不變（乘算層），斑塊是暗＝雲影。
     用乘算而不是加算：黃昏的光影是**被雲擋住**，不是多一盞燈。 */
  g.fillStyle = '#ffffff'; g.fillRect(0, 0, S, S);
  for (var i = 0; i < 16; i++) {
    var x = rnd() * S, y = rnd() * S, r = 80 + rnd() * 150;
    var rg = g.createRadialGradient(x, y, 0, x, y, r);
    var a = 0.08 + rnd() * 0.15;
    rg.addColorStop(0, 'rgba(6,10,20,' + a + ')');
    rg.addColorStop(0.6, 'rgba(8,12,22,' + a * 0.4 + ')');
    rg.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = rg; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
  }
  _light = tex(c);
  return _light;
}

var _glow = null;
/** 發光精靈用的柔光圓（火光、煙）。中心不透明、邊緣全透明。 */
export function glowTex() {
  if (_glow !== null || typeof document === 'undefined') return _glow;
  var c = cv(64, 64), g = c.getContext('2d');
  var gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, 'rgba(255,255,255,1)');
  gr.addColorStop(.32, 'rgba(255,255,255,.45)');
  gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
  _glow = tex(c, false);
  return _glow;
}

var _beam = null;
/** 探照燈束用的漸層：**頂端（燈口）亮、往下淡出**。
    加算混合下黑＝不加，所以這張圖的黑色部分等於「光在這裡已經散掉了」。
    一根等亮度的錐體讀成一塊塑膠，有漸層才讀成穿過塵的光。 */
export function beamTex() {
  if (_beam !== null || typeof document === 'undefined') return _beam;
  var c = cv(16, 128), g = c.getContext('2d');
  var gr = g.createLinearGradient(0, 0, 0, 128);
  gr.addColorStop(0, '#ffffff');
  gr.addColorStop(0.30, '#78828c');
  gr.addColorStop(0.72, '#262a2e');
  gr.addColorStop(1, '#000000');
  g.fillStyle = gr; g.fillRect(0, 0, 16, 128);
  _beam = tex(c, false);
  return _beam;
}

/** 灰燼天空：地平線那條冷青灰是散在塵裡的城市餘光。

    2026-08-04 從黃昏的銅色換成冷夜（使用者：場地要配合機甲的冷調）。
    排法沒變、只換色相：頂端近黑的藍 → 中段的藍灰 → 地平線上方一條
    **青灰**（最亮的一段）→ 地平線以下再暗下去。

    ⚠️ 地平線那條亮帶**不能拿掉也不能調成純藍**：它是遠景剪影唯一的
    背光來源，沒有它整圈天際線會跟天空糊成一片（見下面色標位置那段）。 */
export function skyTex() {
  if (_sky !== null || typeof document === 'undefined') return _sky;
  var c = cv(8, 256), g = c.getContext('2d');
  var gr = g.createLinearGradient(0, 0, 0, 256);
  /* ⚠️ **色標的位置要對到球上的地平線**，而地平線在 `SphereGeometry` 的
     UV 正中間（v = 0.5）。照抄原型的 `.76` 會把那條銅色帶放到**地平線
     以下**，於是玩家永遠看不到它，天空從頭到尾是一片近黑的藍 ——
     2026-08-04 實看時「畫面還是很像夜晚」的真因就是這個。

     現在的排法：天頂近黑 → 地平線上方一條暖銅（.48–.56，最亮的一段
     壓在地平線正上方）→ 地平線以下再暗下去。 */
  var stops = ['#04060d', '#0a101c', '#1c2230', '#36505f', '#4a6c7e', '#1a2630', '#0e1418'];
  var at = [0, .30, .44, .49, .54, .64, 1];
  for (var i = 0; i < stops.length; i++) gr.addColorStop(at[i], stops[i]);
  g.fillStyle = gr; g.fillRect(0, 0, 8, 256);
  _sky = tex(c, false);
  return _sky;
}

/** 場外的遠景廢墟：走不到、但看得到的天際線。
    邊界因此不是「一條線」而是「更遠的地方」—— 空曠場地的視覺條件。 */
export function farRuins(R, color, opts?) {
  /* ⚠️ **高度不可以跟距離等比放大**（`h *= k` 那一行的 `k = d/R`）。
     等比＝所有剪影在畫面上一樣高，於是地平線讀成**一道等高的白牆**，
     深度感歸零 —— 2026-08-04 實看抓到的，症狀是「背景像一張貼上去的紙」。
     正確做法是讓遠的東西**在畫面上更低**（`hk < 1`），這是大氣透視的
     幾何那一半（另一半是顏色，在 `applyTheme`）。 */
  opts = opts || {};
  var N = opts.n || 150, D0 = opts.d0 || 1.08, D1 = opts.d1 || 1.9, HK = opts.hk === undefined ? 0.62 : opts.hk;
  var grp = new THREE.Group();
  var mat = new THREE.MeshBasicMaterial({ color: color, fog: true });
  var seed = opts.seed || 4242;
  function rnd() { seed = (seed * 1664525 + 1013904223) & 0x7fffffff; return seed / 0x7fffffff; }
  var box = new THREE.BoxGeometry(1, 1, 1);
  var parts: any[] = [];
  for (var i = 0; i < N; i++) {
    var a = (i / N) * Math.PI * 2 + rnd() * 0.06;
    var d = R * (D0 + rnd() * D1);
    /* 高瘦的塔比矮胖的塊更像城市，而且要有幾棟明顯比別人高
       —— 地平線上要有節奏，不然遠景讀成一圈柵欄。 */
    var k = d / R;
    var w = (10 + rnd() * 26) * k, h = (16 + rnd() * 70) * (rnd() < 0.22 ? 1.7 : 1) * k * HK;
    var m = new THREE.Matrix4().compose(
      new THREE.Vector3(Math.sin(a) * d, h / 2 - 4, Math.cos(a) * d),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(0, a + (rnd() - 0.5) * 0.6, 0)),
      new THREE.Vector3(w, h, w * (0.5 + rnd())));
    var g2 = box.clone().toNonIndexed(); g2.applyMatrix4(m); parts.push(g2);
  }
  var merged = BufferGeometryUtils.mergeBufferGeometries(parts, false);
  var mesh = new THREE.Mesh(merged, mat);
  mesh.frustumCulled = false;
  grp.add(mesh);
  grp.userData.mat = mat;
  return grp;
}
